/* eslint-disable react/jsx-curly-brace-presence */
// =========================================================================================@@
// Last Updated Date: May 17, 2025
// Last Updated By: Steven
// Status Level: 1
// ===========================================================================================

import React, { useContext, useEffect, useRef, useState } from 'react'
import { Modal, Spacer, Spinner, View } from 'oio-react'
import PropTypes from 'prop-types'
import Helmet from 'react-helmet'
import { useRouteMatch } from 'react-router-dom'
import { MagnifyingGlass } from '@phosphor-icons/react'
import { CheckInDeskIcon } from 'assets/icons'
import RoleInvite from 'src/sites/components/Settings/RoleInvite'
import { useUserList } from 'src/core/graphql/hooks'
import CheckInDeskListItemRow from 'src/sites/kits/Events/components/CheckInDeskListItemRow'
import { InitiativeHierarchyContext } from 'src/sites/kits/Utils/InitiativeHierarchy'
import ModalRoute from 'src/sites/kits/Utils/ModalRoute'
import { Button } from 'src/sites/kits/UI2'

const POLL_INTERVAL = 10000

const CheckInDesk = ({ returnUrl }) => {
   const [numCheckedIn, setNumCheckedIn] = useState(0)
   const [numToBeCheckedIn, setNumToBeCheckedIn] = useState(0)
   const [searchValue, setSearchValue] = useState(null)
   const searchFieldRef = useRef()
   const match = useRouteMatch()

   const { initiative } = useContext(InitiativeHierarchyContext)
   const attendeeRole = initiative.roles.find(role => role.type === 'primary')
   const numRegistrants = initiative.roles.find(r => r.type === 'primary').numActiveUsers

   const { userList, networkStatus, refetch, variables, startPolling, stopPolling } = useUserList(
      {
         limit: 1000,
         userRelationshipFilter: {
            entityType: 'initiative',
            entityId: initiative.id,
            hasRoleParticipantElement: {
               roleType: 'primary',
               status: ['active', 'invited']
            }
         },
         withRelationship: true,
         withRelationshipEntityType: 'initiative',
         withRelationshipEntityId: initiative.id
      },
      {
         // Automatically refetch the list every 10s to emulate realtime behavior
         pollInterval: POLL_INTERVAL
      }
   )

   // TODO: These numbers should not be caclulcated client-side!!
   // Will fail if there are more than `limit` results (1000 in this case)
   useEffect(() => {
      if (!searchValue) {
         const users = userList.items
         const { numActiveUsers } = initiative.roles.find(r => r.type === 'primary')

         const newNumCheckedIn = users.reduce(
            (count, user) => (user.relationship.isCheckedIn ? count + 1 : count),
            0
         )

         const newNumActiveRegistrantsCheckedIn = users.reduce((count, user) => {
            if (user.relationship.isCheckedIn) {
               const roleParticipantElement = user.relationship.elements.find(element => element.__typename === 'RelationshipElementRoleParticipant' && element.role.id === attendeeRole.id)

               return roleParticipantElement.status === 'active' ? count + 1 : count
            }

            return count
         }, 0)

         setNumCheckedIn(newNumCheckedIn)
         setNumToBeCheckedIn(Math.max(numActiveUsers - newNumActiveRegistrantsCheckedIn, 0))
      }
   }, [userList])

   useEffect(() => {
      // There are some weird side effects when we are polling and refetching
      // at the same time. Hopefully this is addressed by Apollo sometime. But
      // even if it isn't, we will move to use real realtime logic instead of
      // polling anyway in the near future.
      stopPolling()
      refetch({ search: searchValue }).then(() => {
         startPolling(POLL_INTERVAL)
      })
   }, [searchValue])

   const handleSearch = async () => {
      const searchFieldValue = searchFieldRef.current.value
      setSearchValue(searchFieldValue || null)
   }

   const handleSearchKeyPress = (event) => {
      if (event.key === 'Enter') {
         event.preventDefault()
         handleSearch()
      }
   }

   const clearSearch = () => {
      searchFieldRef.current.value = ''
      handleSearch()
   }

   const initialLoading = networkStatus <= 2

   return (
      <div className="flex flex-col lg:flex-row h-screen overflow-auto">
         <Helmet title={`Check-in Desk | ${initiative.name}`} />
         <div
            className="flex flex-col justify-between w-full lg:w-[300px] lg:h-full lg:overflow-y-auto p-6 md:p-8 pt-10 lg:border-r border-solid border-neutral-200">
            <div className="flex flex-col gap-4 w-full pb-4">
               <View
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  width="36px"
                  height="36px"
                  borderRadius="50%"
                  backgroundColor="#000">
                  <CheckInDeskIcon width="20px" height="20px" strokeWidth="2px" color="#fff" />
               </View>
               <div>
                  <div className="text-2xl lg:text-xl tracking-tight font-semibold">
                     Check-in Desk
                  </div>
                  <div className="relative -top-1 text-xl lg:text-lg tracking-tight opacity-60">
                     {initiative.name}
                  </div>
               </div>
               {/* <div className="text-sm font-medium my-4">
                  {initiative.startDateTime}
                  {initiative.endDateTime && ` - ${initiative.endDateTime}`}
               </div> */}
               <div className="max-w-3xl text-sm text-neutral-500 text-balance">
                  {'Welcome to the Check-In Desk. Confirm the attendance of people who have registered for this event.'}
               </div>
               <div className="w-full flex flex-row lg:flex-col gap-2 mt-2 lg:mt-4">
                  <Button
                     linkTo={`${match.url}/invite`}
                     size="md"
                     className="bg-black text-white hover:bg-neutral-800">
                     New Attendee
                  </Button>
                  <Button
                     linkTo={returnUrl}
                     size="md"
                     className="bg-neutral-100 text-neutral-500 hover:bg-neutral-200">
                     Go to Event Page
                  </Button>
               </div>
            </div>
            <div className="flex sm:grid grid-cols-3 lg:flex flex-col bg-emerald-50/50 lg:px-4 rounded-lg text-emerald-500 text-xs border border-solid border-emerald-100">
               <div className="flex flex-row items-center lg:items-start lg:flex-col px-5 lg:px-0 py-4">
                  <div className="text-xl lg:text-3xl font-semibold mr-2">{numRegistrants.toString()}</div>
                  <div className="font-medium lg:mt-1">
                     <span className="hidden lg:inline">Attendees </span>
                     Registered
                  </div>
               </div>
               <div
                  className="flex flex-row items-center lg:items-start lg:flex-col px-5 lg:px-0 py-4
                  border-t sm:border-l sm:border-t-0 lg:border-l-0 lg:border-t border-solid border-emerald-100 lg:border-emerald-200">
                  <div className="text-xl lg:text-3xl font-semibold mr-2">{numCheckedIn.toString()}</div>
                  <div className="font-medium lg:mt-1">Checked-in</div>
               </div>
               <div
                  className="flex flex-row items-center lg:items-start lg:flex-col px-5 lg:px-0 py-4
                  border-t sm:border-l sm:border-t-0 lg:border-l-0 lg:border-t border-solid border-emerald-100 lg:border-emerald-200">
                  <div className="text-xl lg:text-3xl font-semibold mr-2">
                     {numToBeCheckedIn.toString()}
                  </div>
                  <div className="font-medium lg:mt-1">Not Checked-in</div>
               </div>
            </div>
         </div>
         <div
            className="flex justify-center w-full lg:w-[calc(100%-300px)] lg:h-full lg:overflow-y-auto bg-neutral-100 p-2 md:p-8">
            <div className="w-full max-w-4xl">
               <div className="w-full bg-white border border-solid border-neutral-200 shadow-sm rounded-xl p-4">
                  <div className="relative flex items-center gap-2">
                     <div className="absolute left-3 top-3">
                        <MagnifyingGlass size={16} className="text-neutral-500" />
                     </div>
                     <input
                        ref={searchFieldRef}
                        onKeyDown={handleSearchKeyPress}
                        placeholder="Search Attendees"
                        className="w-full text-sm md:text-base pl-8 pr-4 py-2 bg-neutral-50 border border-solid border-neutral-200/70 hover:bg-neutral-100 rounded-lg focus:border-neutral-400 transition-all duration-100"
                     />
                  </div>
                  {variables.search && (networkStatus === 6 || networkStatus === 7) && (
                     <div className="flex flex-row justify-between w-full p-4 bg-emerald-50 rounded-lg my-2 text-sm">
                        <div>
                           Found
                           <b className="text-emerald-600">
                              {` ${userList.items.length} results `}
                           </b>
                           containing
                           <b className="text-emerald-600">{` ${variables.search}`}</b>
                        </div>
                        <div>
                           <button type="button" onClick={clearSearch} className="cursor-pointer">
                              <u>Clear Current Search</u>
                           </button>
                        </div>
                     </div>
                  )}
                  <div className="w-full mt-1">
                     {initialLoading && (
                        <View width="100%" textAlign="center" padding="48px">
                           <Spinner color="#ccc" width="24px" height="24px" />
                        </View>
                     )}
                     {!initialLoading &&
                        userList.items.map((user) => {
                           const roleParticipantElement = user.relationship.elements.find(
                              element => element.__typename === 'RelationshipElementRoleParticipant' &&
                                 element.role.id === attendeeRole.id
                           )

                           return (
                              <CheckInDeskListItemRow
                                 key={user.id}
                                 checkedIn={user.relationship.isCheckedIn}
                                 initiativeId={initiative.id}
                                 relationshipId={user.relationship.id}
                                 relationshipRoleParticipantStatus={roleParticipantElement.status}
                                 roleId={attendeeRole.id}
                                 userId={user.id}
                                 userAvatarUrl={user.avatarMedia?.file.thumbnailUrlW48}
                                 userFullName={user.fullName}
                                 userOrganization={user.organization}
                                 userPosition={user.position}
                              />
                           )
                        })}
                  </div>
               </div>
               <Spacer size="8" />
            </div>
         </div>
         <ModalRoute path={`${match.url}/invite`}>
            {({ open, onCloseComplete, onCloseTrigger }) => (
               <Modal
                  borderRadius="6px"
                  width="100%[a-b] 330px[c-f]"
                  onCloseComplete={onCloseComplete}
                  onCloseTrigger={onCloseTrigger}
                  open={open}>
                  <RoleInvite
                     onCloseButtonClick={onCloseTrigger}
                     onInvite={() => {
                        onCloseTrigger()
                        clearSearch()
                     }}
                     roleId={attendeeRole.id}
                     roleName={attendeeRole.name}
                     targetId={initiative.id}
                     targetType="initiative"
                  />
               </Modal>
            )}
         </ModalRoute>
      </div>
   )
}

CheckInDesk.propTypes = {
   returnUrl: PropTypes.string.isRequired
}

export default CheckInDesk
